import classNames from "classnames";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { LuShare2 } from "react-icons/lu";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import {
  useGetClientPortalQuery,
  useMarkClientUploadsSeenMutation,
  useShareWithClientMutation,
} from "../../slices/ClientPortalSlice";
import { documentsSlice } from "../../slices/DocumentsSlice";
import { filesSlice } from "../../slices/FilesSlice";
import { PortalFileFields } from "./clientPortal.types";
import {
  countPortalFiles,
  isNewClientUpload,
  PortalFileFilter,
  portalFileState,
  portalSummary,
} from "./clientPortal.utils";
import { errorText } from "./PortalModal";

/**
 * Both lists of a client's files (profile Files card = filesSlice, Files tab and
 * the Client portal widget's Files tab = documentsSlice) load again, so a share
 * change made in one shows in the other.
 */
export const useRefreshClientFiles = () => {
  const dispatch = useDispatch();
  return () => {
    dispatch(documentsSlice.util.invalidateTags(["ClientFiles"]));
    dispatch(filesSlice.util.invalidateTags(["ContactFiles"]));
  };
};

/**
 * Portal state for a client's file list (profile Files card, Client portal
 * widget's Files tab): the filter, which client uploads are new (kept for this
 * visit even after they're marked as seen) and sharing.
 */
export const usePortalFiles = <T extends PortalFileFields & { id: string }>(
  customerId: string,
  contactFiles: T[] | undefined,
  refetch: () => void,
) => {
  const [filter, setFilter] = useState<PortalFileFilter>("all");
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [markSeen] = useMarkClientUploadsSeenMutation();
  const [share, shareState] = useShareWithClientMutation();
  // Same query as the portal sections (shared by RTK Query): is anyone signed in?
  const { data: portal } = useGetClientPortalQuery(
    { customerId },
    { skip: !customerId },
  );
  const marked = useRef(false);
  const refreshAll = useRefreshClientFiles();

  useEffect(() => {
    const unseen = (contactFiles ?? [])
      .filter(isNewClientUpload)
      .map((f) => f.id);
    if (!unseen.length) return;
    setNewIds((s) => new Set([...Array.from(s), ...unseen]));
    if (marked.current || !customerId) return;
    marked.current = true;
    const timer = window.setTimeout(() => void markSeen(customerId), 3000);
    return () => window.clearTimeout(timer);
  }, [contactFiles, customerId, markSeen]);

  const counts = useMemo(
    () => countPortalFiles(contactFiles ?? []),
    [contactFiles],
  );

  const toggleShare = async (file: T) => {
    const shared = portalFileState(file) !== "shared";
    const result = await share({ documentIds: [file.id], shared });
    if ("error" in result)
      return toast(errorText(result.error), { type: "error" });
    // Sharing never emails anyone: it only puts the file in the client's
    // portal. Owner 2026-10-28 (Loom 41494081) asked what it does when the
    // client has no portal yet, so say so.
    const portalActive = portalSummary(portal?.people ?? []) === "active";
    toast(
      !shared
        ? "Removed from the client portal"
        : portalActive
        ? "Shown in the client portal (no email was sent)"
        : "Shown in the client portal. The client will see it once they're invited and sign in.",
      { type: "success" },
    );
    refetch();
    refreshAll();
  };

  return {
    filter,
    setFilter,
    counts,
    newIds,
    toggleShare,
    isSharing: shareState.isLoading,
  };
};

export const PortalFilterChips: FC<{
  filter: PortalFileFilter;
  setFilter: (f: PortalFileFilter) => void;
  counts: { all: number; fromClient: number; shared: number };
  total: number;
}> = ({ filter, setFilter, counts, total }) => {
  if (!counts.fromClient && !counts.shared) return null;
  const chip = (key: PortalFileFilter, label: string, n: number) => (
    <button
      key={key}
      type={"button"}
      onClick={() => setFilter(key)}
      aria-pressed={filter === key}
      className={classNames(
        "h-7 whitespace-nowrap rounded-lg px-2.5 text-xs",
        filter === key
          ? "bg-[#F0EDFF] font-extrabold text-[#4B2FC7]"
          : "border border-[#DCE2EA] font-bold text-[#3A4256] hover:bg-[#F7F8FA]",
      )}>
      {label} · {n}
    </button>
  );
  return (
    <div className={"mb-2 flex flex-wrap gap-1.5"}>
      {chip("all", "All", total)}
      {chip("fromClient", "From client", counts.fromClient)}
      {chip("shared", "Shared", counts.shared)}
    </div>
  );
};

/** "New" + "From client · Maria" / "Shared with client" under a file name. */
export const PortalFileTags: FC<{ file: PortalFileFields; isNew: boolean }> = ({
  file,
  isNew,
}) => {
  const state = portalFileState(file);
  if (state === "firm") return null;
  return (
    <div className={"mt-1 flex flex-wrap items-center gap-1.5"}>
      {isNew && (
        <span
          className={
            "rounded-full bg-[#6B4EFF] px-2 py-px text-[10.5px] font-extrabold text-white"
          }>
          New
        </span>
      )}
      {state === "fromClient" ? (
        <span
          className={
            "rounded-full bg-[#FFF4DB] px-2 py-px text-[11px] font-bold text-[#8A5A00]"
          }
          title={file.notes ? `Note: ${file.notes}` : undefined}>
          From client
          {file.portalUploaderName ? ` · ${file.portalUploaderName}` : ""}
        </span>
      ) : (
        <span
          className={
            "rounded-full bg-[#F0EDFF] px-2 py-px text-[11px] font-bold text-[#5B3DF5]"
          }>
          Shared with client
        </span>
      )}
      {state === "fromClient" && file.notes && (
        <span className={"truncate text-[11px] italic text-[#6B7185]"}>
          “{file.notes}”
        </span>
      )}
    </div>
  );
};

/** Row button: put a firm file in the client's portal, or take it out again. */
export const ShareToggleButton: FC<{
  file: PortalFileFields;
  disabled?: boolean;
  onToggle: () => void;
}> = ({ file, disabled, onToggle }) => {
  const state = portalFileState(file);
  if (state === "fromClient") return null;
  const shared = state === "shared";
  return (
    <button
      type={"button"}
      disabled={disabled}
      title={
        shared
          ? "Remove from the client portal"
          : "Show in the client portal (no email is sent)"
      }
      aria-label={
        shared
          ? "Remove from the client portal"
          : "Show in the client portal (no email is sent)"
      }
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={classNames(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border disabled:opacity-60",
        shared
          ? "border-[#CFC6FF] bg-[#F0EDFF] text-[#6B4EFF]"
          : "border-[#E4E7EC] text-[#9097A8] hover:bg-[#F4F2FF] hover:text-[#6B4EFF]",
      )}>
      <LuShare2 size={15} />
    </button>
  );
};
